import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { E_FOLDER_TYPES, E_QPROPS, E_VIEW_TYPE, HTTP_ERRORS } from '@shared/constants/enums.const';
import { HomeComponent } from '../home/home.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NewsComponent } from '../news/news.component';
import { BaseComponent } from '@shared/classes/base.class';
import { ContentService } from '../../services/content.service';
import {
  IDetails_Response,
  IHomeResponse,
  INews_Response,
  IRelated_Response,
  TPageDataWithHome,
} from '@shared/constants/models.const';
import { NewsDetailsComponent } from '../news-details/news-details.component';
import { catchError, EMPTY, take } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PublicationDetailsComponent } from '../publication-details/publication-details.component';
import { PublicationListComponent } from '../publication-list/publication-list.component';
import { ContentPageComponent } from '../content-page/content-page.component';
import { Title } from '@angular/platform-browser';
import { TranslationService } from '../../services/translations/translation.service';
import { E_TRANS_KEYS } from '../../services/translations/translation.keys';
import { JurisdictionListComponent } from '../jurisdiction-list/jurisdiction-list.component';
import { ResourcesListComponent } from '../resources-list/resources-list.component';
import { ResourcesDetailsComponent } from '../resources-details/resources-details.component';
import { JurisdictionDetailsComponent } from '../jurisdiction-details/jurisdiction-details.component';
import { ImportantDatesDetailComponent } from '../important-dates-detail/important-dates-detail.component';
import { MeetingListComponent } from '../meeting-list/meeting-list.component';
import { HelperService } from '../../services/helper.service';
import { Error404Component } from '../error-page/error-404/error-404.component';
import { Location } from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'app-chose-page-type',
  imports: [
    HomeComponent,
    NewsComponent,
    NewsDetailsComponent,
    MatProgressSpinner,
    PublicationDetailsComponent,
    PublicationListComponent,
    ContentPageComponent,
    JurisdictionListComponent,
    ResourcesListComponent,
    ResourcesDetailsComponent,
    JurisdictionDetailsComponent,
    ImportantDatesDetailComponent,
    MeetingListComponent,
    Error404Component,
  ],
  templateUrl: './chose-page-type.component.html',
  styleUrl: './chose-page-type.component.scss',
})
export class ChosePageTypeComponent extends BaseComponent implements OnInit {
  selectedView: E_VIEW_TYPE = E_VIEW_TYPE.None;
  E_VIEW_TYPE = E_VIEW_TYPE;
  selectedUrl = '';
  has404Error = false;
  override pageData: INews_Response | IDetails_Response | IRelated_Response | IHomeResponse | undefined;
  readonly location: Location = inject(Location);

  constructor(
    override router: Router,
    private CONTENT: ContentService,
    private route: ActivatedRoute,
    private titleService: Title,
    private TRANSLATION: TranslationService,
    private HELPER: HelperService,
  ) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();

    this.location.subscribe(event => {
      if (event.pop) {
        this.HELPER.hasTags(true);
      }
    });


    this.setSelectedUrl();
    this.getData();
  }

  setSelectedUrl(): void {
    const urlArr = this.getUrlArray();
    this.selectedUrl = urlArr.slice(1).join('/');
  }

  override onReload() {
    this.setSelectedUrl();
    this.getData();
  }

  override getData(): void {
    this.HELPER.selectCategoryStickyState = false;
    this.HELPER.rootFolderId = 0;
    this.pageData = undefined;
    this.selectedView = E_VIEW_TYPE.None;
    this.has404Error = false;

    // for compatibility .../en-ca -> .../en-ca/home
    if (this.HELPER.getUrlArray().length === 1) {
      this.router.navigate([this.HELPER.getFrLocFromUrl(), 'home']);
      return;
    }

    if (this.isHome) {
      this.getHomeData();
      this.titleService.setTitle(this.TRANSLATION.getTranslation(E_TRANS_KEYS.HomeTitle));
    } else {
      this.getPageByUrl();
    }
  }

  getPageByUrl(): void {

    let isTake5 = !this.HELPER.ifContains(this.selectedUrl, ['news', 'jurisdictions']);

    this.CONTENT.getPageByUrl(
      this.selectedUrl,
      this.HELPER.getProps(isTake5),
    )
      .pipe(take(1),
        catchError((err) => {
          const errCode = err?.error?.code;
          if (errCode && errCode === HTTP_ERRORS.PageNotFound) {
            this.has404Error = true;
          } else {
            this.router.navigate([this.HELPER.getUrl(`error/${HTTP_ERRORS.somethingWentWrong}`)]);
          }
          return EMPTY;
        }),
      )
      .subscribe((data: TPageDataWithHome) => {
        this.HELPER.changeFrClicked = false;
        this.has404Error = false;
        if (data.title) {
          this.titleService.setTitle(data.title);
        }
        this.setGlobalContentType(data);
        this.chooseSelectViewAndData(data);
        this.HELPER.$pageData.next(data);
      });
  }


  getHomeData(): void {
    this.HELPER.contentType = E_FOLDER_TYPES.Home;
    const tags = this.HELPER.getTagsValue();

    const props = tags
      ? { [E_QPROPS.tagType]: tags }
      : {};

    this.CONTENT.getHomePage(props).pipe(take(1)).subscribe(data => {
      this.HELPER.changeFrClicked = false;
      this.selectedView = E_VIEW_TYPE.Home;
      this.pageData = data as IHomeResponse;
      this.HELPER.$pageData.next(data);
    });
  }

  chooseSelectViewAndData(data: TPageDataWithHome): void {

    this.selectedView = E_VIEW_TYPE.ContentPage;
    this.pageData = data as IDetails_Response;

    if (this.isNewsList(data)) {
      this.selectedView = E_VIEW_TYPE.NewsList;
      this.pageData = data as INews_Response;
    }

    if (this.isNewsDetail(data)) {
      this.selectedView = E_VIEW_TYPE.NewsDetail;
      this.pageData = data as IDetails_Response;
    }

    if (this.isPublicationDetail(data)) {
      this.selectedView = E_VIEW_TYPE.PublicationDetail;
      this.pageData = data as IDetails_Response;
    }

    if (this.isPublicationList(data)) {
      this.selectedView = E_VIEW_TYPE.PublicationList;
      this.pageData = data as IDetails_Response;
    }

    if (this.isContentPage(data)) {
      this.selectedView = E_VIEW_TYPE.ContentPage;
      this.pageData = data as IDetails_Response;
    }

    if (this.isMeetingsList(data)) {
      this.selectedView = E_VIEW_TYPE.MeetingList;
      this.pageData = data as IDetails_Response;
    }

    if (this.isMeetingsDetail(data)) {
      this.selectedView = E_VIEW_TYPE.MeetingDetail;
      this.pageData = data as IDetails_Response;
    }


    if (this.isStandardsList(data)) {
      this.selectedView = E_VIEW_TYPE.StandardsList;
      this.pageData = data as IDetails_Response;
    }

    if (this.isStandardsDetail(data)) {
      this.selectedView = E_VIEW_TYPE.StandardsDetail;
      this.pageData = data as IDetails_Response;
    }


    if (this.isProjectList(data) || this.isProjectDetail(data)) {
      this.selectedView = E_VIEW_TYPE.ProjectList;
      this.pageData = data as IDetails_Response;
    }

    if (this.isJurisdictionList(data)) {
      this.selectedView = E_VIEW_TYPE.JurisdictionList;
      this.pageData = data as IDetails_Response;
    }

    if (this.isJurisdictionDetail(data)) {
      this.selectedView = E_VIEW_TYPE.JurisdictionDetail;
      this.pageData = data as IDetails_Response;
    }

    if (this.isResourceList(data)) {
      this.selectedView = E_VIEW_TYPE.ResourceList;
      this.pageData = data as IDetails_Response;
    }

    if (this.isResourceDetail(data)) {
      this.selectedView = E_VIEW_TYPE.ResourceDetail;
      this.pageData = data as IDetails_Response;
    }

    if (this.isImportantDatesDetail(data)) {
      this.selectedView = E_VIEW_TYPE.ImportantDatesDetail;
      this.pageData = data as IDetails_Response;
    }

    if (this.isDiscussionDetail(data)) {
      this.selectedView = E_VIEW_TYPE.DiscussionDetail;
      this.pageData = data as IDetails_Response;
    }

  }

  setGlobalContentType(data: TPageDataWithHome): void {
    this.HELPER.rootFolderId = 0;
    const rootFolderIndex = 0;
    if (data.breadcrumbs && data.breadcrumbs.length) {
      this.HELPER.rootFolderId = data.breadcrumbs[rootFolderIndex].id;
    } else {
      this.HELPER.rootFolderId = data.id;
    }


    if (data.type || data.folderType) {
      this.HELPER.contentType = data.type === E_FOLDER_TYPES.Folder
        ? data.folderType
        : data.type;
    } else {
      this.HELPER.contentType = E_FOLDER_TYPES.Home;
    }
  }

  isNewsList(data: any): boolean {
    return data.items && data.folderType === E_FOLDER_TYPES.News;
  }

  isNewsDetail(data: any): boolean {
    return !data.items && data.type === E_FOLDER_TYPES.News;
  }

  isPublicationDetail(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Publication && !data.folderType;
  }

  isPublicationList(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Folder && data.folderType === E_FOLDER_TYPES.Publication;
  }

  isMeetingsList(data: any): boolean {
    return (data.type === E_FOLDER_TYPES.Folder) && ((data.folderType === E_FOLDER_TYPES.Meeting) || (data.folderType === E_FOLDER_TYPES.ImportantDates));
  }

  isMeetingsDetail(data: any): boolean {
    return ((data.type === E_FOLDER_TYPES.Meeting) || (data.type === E_FOLDER_TYPES.ImportantDates)) && !data.folderType;
  }

  isContentPage(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Folder && data.folderType === E_FOLDER_TYPES.Folder && (!data.items || data.items.length === 0);
  }

  isStandardsList(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Folder && data.folderType === E_FOLDER_TYPES.Standards;
  }

  isStandardsDetail(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Standards && !data.folderType;
  }

  isProjectList(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Folder && data.folderType === E_FOLDER_TYPES.Project;
  }

  isProjectDetail(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Project && !data.folderType;
  }

  isJurisdictionList(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Folder && data.folderType === E_FOLDER_TYPES.Jurisdiction;
  }

  isJurisdictionDetail(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Jurisdiction && !data.folderType;
  }

  isResourceList(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Folder && data.folderType === E_FOLDER_TYPES.Resource;
  }

  isResourceDetail(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Resource && !data.folderType;
  }

  isImportantDatesDetail(data: any): boolean {
    return data.type === E_FOLDER_TYPES.ImportantDates && !data.folderType;
  }

  isDiscussionDetail(data: any): boolean {
    return data.type === E_FOLDER_TYPES.Discussion && !data.folderType;
  }


}
